/**
 * LOB (Line of Business) API — the TOP level of the hierarchy.
 *
 * A LOB groups functions (api/functions.js). It carries no members, shift or
 * screen-capture settings of its own; a LOB head implicitly heads every
 * function beneath it.
 */

import apiClient from './client';
import { loadOptions, peekOptions, invalidateOptions } from './optionsCache';

const CACHE_PREFIX = 'lobs';

export const lobsApi = {
  /**
   * List LOBs visible to the caller (superadmin: all; head: the LOBs they head,
   * plus the parents of any function they head). Each row carries a
   * `functionCount`.
   */
  async list(params) {
    const response = await apiClient.get('/api/portal/lobs', { params });
    return response.data;
  },

  /**
   * Same as `list`, but served from the session cache (api/optionsCache.js).
   * For the filter bar, which re-mounts on every page and does not need a fresh
   * read. Management screens keep using `list`.
   */
  listCached(params) {
    return loadOptions(cacheKey(params), () => lobsApi.list(params));
  },

  /** Synchronous cache hit for `listCached`, or undefined. */
  peekList(params) {
    return peekOptions(cacheKey(params));
  },

  /**
   * Create a LOB (superadmin).
   * @param {Object} data - { name, description }
   */
  async create(data) {
    const response = await apiClient.post('/api/portal/lobs', data);
    invalidateOptions(CACHE_PREFIX);
    return response.data;
  },

  /**
   * Update a LOB (superadmin).
   * @param {string} lobId
   * @param {Object} data - any subset of { name, description, isActive }
   */
  async update(lobId, data) {
    const response = await apiClient.put(`/api/portal/lobs/${lobId}`, data);
    invalidateOptions(CACHE_PREFIX);
    return response.data;
  },

  /** Delete a LOB (superadmin). Rejected with 409 while it still has functions. */
  async remove(lobId) {
    const response = await apiClient.delete(`/api/portal/lobs/${lobId}`);
    invalidateOptions(CACHE_PREFIX);
    return response.data;
  },

  /** Functions under this LOB, clamped to what the caller may see. */
  async listFunctions(lobId, params) {
    const response = await apiClient.get(`/api/portal/lobs/${lobId}/functions`, { params });
    return response.data;
  },

  // --- Heads (superadmin) ---

  async listHeads(lobId) {
    const response = await apiClient.get(`/api/portal/lobs/${lobId}/heads`);
    return response.data;
  },

  // Head changes move LOBs in and out of a head's own scope, so they invalidate
  // the filter cache too.
  async addHeads(lobId, adminIds) {
    const response = await apiClient.post(`/api/portal/lobs/${lobId}/heads`, { adminIds });
    invalidateOptions(CACHE_PREFIX);
    return response.data;
  },

  async removeHead(lobId, adminId) {
    const response = await apiClient.delete(`/api/portal/lobs/${lobId}/heads/${adminId}`);
    invalidateOptions(CACHE_PREFIX);
    return response.data;
  },
};

function cacheKey(params) {
  return params ? `${CACHE_PREFIX}:${JSON.stringify(params)}` : CACHE_PREFIX;
}

export default lobsApi;
